import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, Save, X, Upload, Cookie } from 'lucide-react';
import { supabase } from '../supabaseClient';
import UserLayout from './user/UserLayout';
import { showNotification, showError } from '../utils/notificationHandler';

const UserSettings = () => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: '',
    companyName: '',
    mhsAccountNumber: '',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [cookieConsent, setCookieConsent] = useState(null);
  const [cookieConsentDate, setCookieConsentDate] = useState(null);

  // New state for address handling
  const [addressInput, setAddressInput] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [structuredAddress, setStructuredAddress] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const history = useHistory();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const script = document.createElement('script');
      const apiKey = window.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeAutocomplete;
      document.body.appendChild(script);
    };
  
    const initializeAutocomplete = () => {
      const input = document.getElementById("new-address");
      if (input) {
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: "au" }
        });
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) {
            console.log("Returned place contains no geometry");
            return;
          }
          const addressComponents = place.address_components;
          const formattedAddress = {
            address_line1: '',
            city: '',
            state: '',
            postal_code: '',
          };
    
          for (let component of addressComponents) {
            const componentType = component.types[0];
            switch (componentType) {
              case "street_number":
                formattedAddress.address_line1 = `${component.long_name} ${formattedAddress.address_line1}`;
                break;
              case "route":
                formattedAddress.address_line1 += component.short_name;
                break;
              case "postal_code":
                formattedAddress.postal_code = component.long_name;
                break;
              case "locality":
                formattedAddress.city = component.long_name;
                break;
              case "administrative_area_level_1":
                formattedAddress.state = component.short_name;
                break;
            }
          }
          setSelectedAddress(place.formatted_address);
          setAddressInput(place.formatted_address);
          setStructuredAddress(formattedAddress);
        });
      }
    };
  
    if (!window.google) {
      loadGoogleMapsScript();
    } else {
      initializeAutocomplete();
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
  
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();
  
        if (error) throw error;
  
        setFormData({
          fullName: data.full_name || '',
          email: data.email || '',
          role: data.role || '',
          companyName: data.company_name || '',
          mhsAccountNumber: data.mhs_account_number || '',
        });
  
        if (data.avatar_url) {
          setProfileImage(data.avatar_url);
        }

        setCookieConsent(data.cookie_consent);
        setCookieConsentDate(data.cookie_consent_date);

        fetchDeliveryAddresses(user.id);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      showError(error, 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryAddresses = async (userId) => {
    try {
      // First get the user's deb_id (company identifier)
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('deb_id')
        .eq('id', userId)
        .single();

      if (accountError) throw accountError;
      if (!accountData.deb_id) {
        console.warn('No company deb_id found for this account');
        return;
      }

      // Fetch all delivery addresses for this company
      const { data, error } = await supabase
        .from('delivery_addresses')
        .select('*')
        .eq('company_deb_id', accountData.deb_id);

      if (error) throw error;

      setDeliveryAddresses(data || []);
      if (data && data.length > 0) {
        setAddressInput(`${data[0].address_line1}, ${data[0].city}, ${data[0].state} ${data[0].postal_code}`);
      }
    } catch (error) {
      console.error('Error fetching delivery addresses:', error);
      showError(error, 'Failed to fetch delivery addresses');
    }
  };

  const handleImageUpload = async (e) => {
    try {
      setUploading(true);
      const file = e.target.files[0];
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      if (!user) throw new Error('No user found');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}${Math.random()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      let { error: uploadError } = await supabase.storage
        .from('main')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('main')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      const { error: updateError } = await supabase
        .from('accounts')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfileImage(publicUrl);
      showNotification('Profile image updated successfully!', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showError(error, 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const parseManualAddress = (addressText) => {
    // Parse manually entered address text into components
    // Expected format: "address line, city, state postcode" or variations
    if (!addressText || addressText.trim() === '') {
      throw new Error('Please enter an address');
    }

    const parts = addressText.split(',').map(p => p.trim());
    
    if (parts.length < 2) {
      throw new Error('Please enter address in format: Street Address, City, State Postcode');
    }

    const addressLine1 = parts[0];
    let city = '';
    let state = '';
    let postalCode = '';

    // Last part should contain state and postal code
    const lastPart = parts[parts.length - 1].trim();
    const statePostcodeMatch = lastPart.match(/([A-Z]{2,3})\s+(\d{4})/);
    
    if (statePostcodeMatch) {
      state = statePostcodeMatch[1];
      postalCode = statePostcodeMatch[2];
      // City is the second-to-last part, or extract from last part before state
      if (parts.length > 2) {
        city = parts[parts.length - 2].trim();
      } else {
        // Extract city from last part (before state/postcode)
        city = lastPart.substring(0, statePostcodeMatch.index).trim();
      }
    } else {
      // Try alternate format: "address, city state postcode"
      if (parts.length >= 2) {
        city = parts[1].trim();
        // Try to extract state and postcode from remaining parts or city
        const cityStateMatch = city.match(/^(.+?)\s+([A-Z]{2,3})\s+(\d{4})$/);
        if (cityStateMatch) {
          city = cityStateMatch[1];
          state = cityStateMatch[2];
          postalCode = cityStateMatch[3];
        }
      }
    }

    if (!postalCode) {
      throw new Error('Could not extract postal code. Please ensure format: Street Address, City, State Postcode (e.g., "123 Main St, Perth, WA 6000")');
    }

    return {
      address_line1: addressLine1,
      city: city || 'Not specified',
      state: state || 'WA',
      postal_code: postalCode
    };
  };

  const handleAddAddress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get the user's deb_id (company identifier)
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('deb_id')
        .eq('id', user.id)
        .single();

      if (accountError) throw accountError;
      if (!accountData.deb_id) throw new Error('No company deb_id found for this account');
      
      let addressData;
      
      // Use structured address from autocomplete if available, otherwise parse manual input
      if (structuredAddress && structuredAddress.postal_code) {
        addressData = structuredAddress;
      } else {
        // Parse manual input
        addressData = parseManualAddress(addressInput);
      }
      
      if (!addressData.postal_code) {
        throw new Error('Please enter a valid address with a postal code');
      }
      
      const { error } = await supabase
        .from('delivery_addresses')
        .insert({ 
          account_id: user.id,
          company_deb_id: accountData.deb_id,
          created_by: user.id,
          address_line1: addressData.address_line1,
          city: addressData.city,
          state: addressData.state,
          postal_code: addressData.postal_code,
          country: 'Australia',
          is_primary: false
        });
  
      if (error) throw error;
  
      // Refresh all company addresses
      fetchDeliveryAddresses(user.id);
      setAddressInput('');
      setSelectedAddress('');
      setStructuredAddress(null);
      showNotification('Address added successfully and shared with all company users', 'success');
    } catch (error) {
      console.error('Error adding address:', error);
      showError(error, 'Failed to add address');
    }
  };

  const handleEditAddress = (id) => {
    setEditingAddressId(id);
    const addressToEdit = deliveryAddresses.find(addr => addr.id === id);
    setAddressInput(`${addressToEdit.address_line1}, ${addressToEdit.city}, ${addressToEdit.state} ${addressToEdit.postal_code}`);
    setSelectedAddress(`${addressToEdit.address_line1}, ${addressToEdit.city}, ${addressToEdit.state} ${addressToEdit.postal_code}`);
    setStructuredAddress({
      address_line1: addressToEdit.address_line1,
      city: addressToEdit.city,
      state: addressToEdit.state,
      postal_code: addressToEdit.postal_code
    });
  };
  
  const handleRemoveAddress = async (addressId) => {
    try {
      const { error } = await supabase
        .from('delivery_addresses')
        .delete()
        .eq('id', addressId);
  
      if (error) throw error;
  
      setDeliveryAddresses(deliveryAddresses.filter(addr => addr.id !== addressId));
      showNotification('Delivery address removed successfully!', 'success');
    } catch (error) {
      console.error('Error removing delivery address:', error);
      showError(error, 'Failed to remove delivery address');
    }
  };
  
  const handleSaveAddress = async () => {
    try {
      let addressData;
      
      // Use structured address from autocomplete if available, otherwise parse manual input
      if (structuredAddress && structuredAddress.postal_code) {
        addressData = structuredAddress;
      } else {
        // Parse manual input
        addressData = parseManualAddress(addressInput);
      }
      
      if (!addressData.postal_code) {
        throw new Error('Please enter a valid address with a postal code');
      }
  
      const { error } = await supabase
        .from('delivery_addresses')
        .update({ 
          address_line1: addressData.address_line1,
          city: addressData.city,
          state: addressData.state,
          postal_code: addressData.postal_code
        })
        .eq('id', editingAddressId);
  
      if (error) throw error;
  
      setDeliveryAddresses(deliveryAddresses.map(addr =>
        addr.id === editingAddressId ? { ...addr, ...addressData } : addr
      ));
      setEditingAddressId(null);
      setAddressInput('');
      setSelectedAddress('');
      setStructuredAddress(null);
      showNotification('Address updated successfully', 'success');
    } catch (error) {
      console.error('Error updating address:', error);
      showError(error, 'Failed to update address');
    }
  };

  const handleCancelEdit = () => {
    setEditingAddressId(null);
    setAddressInput('');
    setSelectedAddress('');
    setStructuredAddress(null);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showError(new Error('Passwords do not match'), 'Passwords do not match');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      showNotification('Password changed successfully', 'success');
      setChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      showError(error, 'Failed to change password');
    }
  };

  const handleChangeEmail = async () => {
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      showNotification('Email change request sent. Please check your new email for confirmation.', 'success');
      setChangingEmail(false);
      setNewEmail('');
    } catch (error) {
      console.error('Error changing email:', error);
      showError(error, 'Failed to change email');
    }
  };

  const handleUpdateCookieConsent = async (accepted) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('accounts')
        .update({
          cookie_consent: accepted,
          cookie_consent_date: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setCookieConsent(accepted);
      setCookieConsentDate(new Date().toISOString());
      showNotification('Cookie preferences updated successfully', 'success');
    } catch (error) {
      console.error('Error updating cookie consent:', error);
      showError(error, 'Failed to update cookie preferences');
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          <span className="ml-3 text-gray-600">Loading settings...</span>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Settings</h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button 
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <span>Settings</span>
            </nav>
          </div>
        </div>
        
        {/* Account Information */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Full Name</label>
              <p className="text-gray-600">{formData.fullName}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
              <p className="text-gray-600">{formData.email}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Role</label>
              <p className="text-gray-600">{formData.role}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Company Name</label>
              <p className="text-gray-600">{formData.companyName}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">MHS Account Number</label>
              <p className="text-gray-600">{formData.mhsAccountNumber}</p>
            </div>
          </div>
          
          <div className="pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Profile Image
            </label>
            <div className="flex items-center space-x-4">
              {profileImage && (
                <img src={profileImage} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-gray-200" />
              )}
              <label
                htmlFor="file-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 bg-brand hover:bg-brand-hover text-brand-dark font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                <span>{uploading ? 'Uploading...' : 'Upload Image'}</span>
                <input 
                  id="file-upload"
                  type='file' 
                  className="hidden" 
                  onChange={handleImageUpload}
                  disabled={uploading}
                  accept="image/*"
                />
              </label>
            </div>
          </div>
        </motion.div>

        {/* Delivery Addresses */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Delivery Addresses</h2>
          {deliveryAddresses.map((address) => (
            <div key={address.id} className="mb-4 p-4 border border-gray-200 rounded-lg flex justify-between items-center hover:bg-gray-50 transition-colors duration-200">
              <p className="text-gray-700">{`${address.address_line1}, ${address.city}, ${address.state} ${address.postal_code}`}</p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditAddress(address.id)}
                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                  title="Edit address"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleRemoveAddress(address.id)}
                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  title="Remove address"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add New Delivery Address
            </label>
            <input
              id="new-address"
              type="text"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              placeholder="e.g., 123 Main Street, Perth, WA 6000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-colors duration-200"
            />
            <p className="mt-1 text-xs text-gray-500">
              Format: Street Address, City, State Postcode (e.g., "123 Main St, Perth, WA 6000")
            </p>
            {editingAddressId ? (
              <div className="mt-3 flex items-center space-x-3">
                <button
                  onClick={handleSaveAddress}
                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  <Save size={16} className="mr-2" />
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  <X size={16} className="mr-2" />
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleAddAddress}
                className="mt-3 inline-flex items-center px-4 py-2 bg-brand hover:bg-brand-hover text-brand-dark font-medium rounded-lg transition-colors duration-200"
              >
                <Plus size={16} className="mr-2" />
                Add Address
              </button>
            )}
          </div>
        </motion.div>

        {/* Change Password */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>
          {changingPassword ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-colors duration-200"
                />
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleChangePassword}
                  className="px-4 py-2 bg-brand hover:bg-brand-hover text-brand-dark font-medium rounded-lg transition-colors duration-200"
                >
                  Change Password
                </button>
                <button
                  onClick={() => {
                    setChangingPassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setChangingPassword(true)}
              className="px-4 py-2 bg-brand hover:bg-brand-hover text-brand-dark font-medium rounded-lg transition-colors duration-200"
            >
              Change Password
            </button>
          )}
        </motion.div>

        {/* Change Email */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Email</h2>
          {changingEmail ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Email Address</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-colors duration-200"
                />
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleChangeEmail}
                  className="px-4 py-2 bg-brand hover:bg-brand-hover text-brand-dark font-medium rounded-lg transition-colors duration-200"
                >
                  Change Email
                </button>
                <button
                  onClick={() => {
                    setChangingEmail(false);
                    setNewEmail('');
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setChangingEmail(true)}
              className="px-4 py-2 bg-brand hover:bg-brand-hover text-brand-dark font-medium rounded-lg transition-colors duration-200"
            >
              Change Email
            </button>
          )}
        </motion.div>

        {/* Cookie Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center mb-6">
            <Cookie className="h-6 w-6 text-brand mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
          </div>
          
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Current Status:</strong> {
                  cookieConsent === null 
                    ? 'Not Set' 
                    : cookieConsent 
                      ? 'All Cookies Accepted' 
                      : 'Optional Cookies Denied'
                }
              </p>
              {cookieConsentDate && (
                <p className="text-xs text-gray-500">
                  Last updated: {new Date(cookieConsentDate).toLocaleDateString('en-AU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <p>Manage your cookie preferences. You can change your consent at any time.</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><strong>Accept:</strong> Allow all cookies including analytics and functional cookies</li>
                <li><strong>Deny:</strong> Only use essential cookies required for site functionality</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                onClick={() => handleUpdateCookieConsent(true)}
                disabled={cookieConsent === true}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  cookieConsent === true
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-brand hover:bg-brand-hover text-brand-dark'
                }`}
              >
                {cookieConsent === true ? 'Currently Accepting' : 'Accept All Cookies'}
              </button>
              <button
                onClick={() => handleUpdateCookieConsent(false)}
                disabled={cookieConsent === false}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  cookieConsent === false
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                {cookieConsent === false ? 'Currently Denying' : 'Deny Optional Cookies'}
              </button>
            </div>
          </div>
        </motion.div>
        
      </div>
    </UserLayout>
  );
};

export default UserSettings;
